/**
 * SOLID Review
 * S — Good. One concern: the runtime contracts of the sockets the frontend Application requires from
 *     Infrastructure — the client's state and its three server gateways. One file because they are one
 *     seam's vocabulary, as the backend's `application/ports/contracts.ts` is.
 * O — Good. A new socket is a new contract block here; existing ones do not change.
 * L — N/A.
 * I — Good. One contract and one derived type per socket.
 * D — Good. The kernel's zod adapter and the Domain's schemas — the Application may know the Domain,
 *     and these are the shapes its shells hand the Domain.
 */
import { z } from 'zod';
import { zodPortContract, type PortInterface, type IConfigZodPortContract } from '@ml3d/kernel/zod';
import { ContextRequest } from '../../domain/ports/schemas/ContextRequest';
import { ResultOf, ResultVoid } from '../../domain/ports/schemas/ResultSchema';
import { Identifier, MeyerRank } from '../../domain/ports/schemas/primitives';
import { ClientDiceRoll } from '../../domain/ports/schemas/ClientTable';
import { ClientChanges, ClientInput } from '../../domain/ports/schemas/ClientChanges';
import { ClientStored, ClientPersist } from '../../domain/ports/schemas/ClientSession';
import {
  PayloadSeatRoom, PayloadRejoin, PayloadRoomCodeLookup, PayloadActiveRoomForPlayer, PayloadMe, PayloadCreatedPlayer,
  RequestSeat, RequestRejoin, RequestLateJoin, RequestHost, RequestPlayer, RequestGuest,
} from '../../domain/ports/schemas/SessionMoves';
import { PayloadActiveRound, PayloadRoomRoster } from '../../domain/ports/schemas/TableEvents';
import {
  PayloadTokenAccount, PayloadSignedIn, PayloadProfile, PayloadReferral, CommandReferral, RequestEmail, RequestSignUp,
  RequestVerifyCode, RequestNickname, RequestEmailCode,
} from '../../domain/ports/schemas/AuthMoves';
import {
  PayloadLegalDocuments, PayloadConsentStatus, RequestRecordConsent, PayloadDataExport, CommandDeleteAccount,
} from '../../domain/ports/schemas/LegalMoves';
import { RequestCookieConsent, CommandCookiePreferences, DtoPreferencesOpened } from '../../domain/ports/schemas/ConsentMoves';
import { DtoCheckout, DtoBalance, PayloadAccountWatch } from '../../domain/ports/schemas/PaymentMoves';
import { RequestAdConfig, DtoAdConfig } from '../../domain/ports/schemas/AdsMoves';
import { DtoHealthReport } from '../../domain/ports/schemas/HealthMoves';
import {
  RequestMediaToken, DtoMediaToken, RequestMediaConnection, RequestMediaPresence, RequestMediaHealth, DtoMediaHealth, RequestMediaUsage, DtoMediaUsage,
} from '../../domain/ports/schemas/MediaMoves';
import { RequestRoundMove, RequestBelieve, RequestRoomRead, AnswerRound, AnswerClaim, PayloadLobbyRound } from '../../domain/ports/schemas/TableMoves';

/**
 * THE APPLICATION'S SOCKETS TOWARDS INFRASTRUCTURE (frontend strict-ports plan, FE1, 2026-10-03). The
 * Application defines what it needs; Infrastructure provides it. Each socket is a
 * `RequiredPortWithDefault` behind its contract, whose Null provider answers `UNAVAILABLE`: a missing
 * adapter degrades a move instead of white-screening the app (the frontend's failure policy).
 *
 * The gateways speak the server's answers in the Domain's schemas, which are the GraphQL types the
 * client selects. Infrastructure PARSES what Apollo hands it into these, where the adapters used to
 * cast. A wrong shape now fails as `CONTRACT` at the edge instead of arriving as an `undefined` in
 * the store.
 */


/**
 * The code a server gateway fails with when the server refused, or the way to it failed. Its message is
 * the server's own and fit to show a player ("…returned a GraphQL error: Not enough players"). Every
 * other code — `CONTRACT`, `INTERNAL`, `DEADLINE` from the guard, `UNAVAILABLE` from a Null provider, a
 * Domain refusal — is a fault the port observer reports, not a sentence for a player.
 */
export const SERVER_REFUSED = 'SERVER';

// ── ClientState: the table and the session, as the stores hold them ─────────────────────────────

/**
 * The client's state. `snapshot` reads it — the table and the session, one consistent read (FE2: a
 * table decision takes the table half, a session decision both) — and `apply` saves a decision's
 * changes, in the order the changes schema states (reset, the fields, activity, the session).
 * React reads the same stores through `ClientStateView`, by reference, because
 * `useSyncExternalStore` needs stable snapshots; every write comes through here.
 */
const ClientStateOps = {
  snapshot: { args: z.tuple([ContextRequest]), result: ResultOf(ClientInput) },
  apply: { args: z.tuple([ContextRequest, ClientChanges]), result: ResultVoid },
} as const;
const clientStateConfig: IConfigZodPortContract<typeof ClientStateOps> = { name: 'ClientState', ops: ClientStateOps };
export const ClientStateContract = zodPortContract(clientStateConfig);
export type IPortClientState = PortInterface<typeof ClientStateOps>;

// ── ClientTabStorage: what the tab keeps across a reload (FE2) ─────────────────────────────────

/**
 * The tab's own storage — per tab, and durable across an OS tab discard: the game it is in and the guest
 * identity it plays as. `stored` reads both; `persist` writes what a decision's `persist` says, in the
 * same step that saved its changes (the plan: "resume persistence is done in the shell's apply step,
 * not by a reactive effect"). LOCAL in fact, contracted like any other socket: storage is a browser
 * resource behind Infrastructure, and the JWT that also lives there never crosses it.
 */
const ClientTabStorageOps = {
  stored: { args: z.tuple([ContextRequest]), result: ResultOf(ClientStored) },
  persist: { args: z.tuple([ContextRequest, ClientPersist]), result: ResultVoid },
} as const;
const clientTabStorageConfig: IConfigZodPortContract<typeof ClientTabStorageOps> = { name: 'ClientTabStorage', ops: ClientTabStorageOps };
export const ClientTabStorageContract = zodPortContract(clientTabStorageConfig);
export type IPortClientTabStorage = PortInterface<typeof ClientTabStorageOps>;

// ── ClientPlayerServer: players, on the server (FE2) ───────────────────────────────────────────

/** A guest player, created by name (`createPlayer`). */
const ClientPlayerServerOps = {
  createPlayer: { args: z.tuple([ContextRequest, RequestGuest]), result: ResultOf(PayloadCreatedPlayer) },
} as const;
const clientPlayerServerConfig: IConfigZodPortContract<typeof ClientPlayerServerOps> = { name: 'ClientPlayerServer', ops: ClientPlayerServerOps };
export const ClientPlayerServerContract = zodPortContract(clientPlayerServerConfig);
export type IPortClientPlayerServer = PortInterface<typeof ClientPlayerServerOps>;

// ── ClientAuthServer: the account, on the auth server (FE2 pulled two ops forward; FE3 the rest) ──

/**
 * The account behind the tab's token, and every call that signs in, signs up or edits it. THE TOKEN
 * STAYS INSIDE INFRASTRUCTURE (FE3): `verifyCode` keeps the JWT the server answers and returns only who
 * signed in; the profile's calls send it; `signOut` forgets it. `account` reads the stored token's player
 * while the token is valid — the auth store's `hydrate`, moved.
 *
 * `me` answers null when there is no player behind the token (none stored, or the server says 401/404),
 * and FAILS when the server could not be asked — two different things since FE3: the first signs the
 * tab out, the second must not. Every refusal the server answered fails as `SERVER` in its own words.
 */
const ClientAuthServerOps = {
  account: { args: z.tuple([ContextRequest]), result: ResultOf(PayloadTokenAccount.nullable()) },
  me: { args: z.tuple([ContextRequest]), result: ResultOf(PayloadMe.nullable()) },
  signOut: { args: z.tuple([ContextRequest]), result: ResultVoid },
  requestLoginCode: { args: z.tuple([ContextRequest, RequestEmail]), result: ResultVoid },
  register: { args: z.tuple([ContextRequest, RequestSignUp]), result: ResultVoid },
  verifyCode: { args: z.tuple([ContextRequest, RequestVerifyCode]), result: ResultOf(PayloadSignedIn) },
  validateReferral: { args: z.tuple([ContextRequest, CommandReferral]), result: ResultOf(PayloadReferral) },
  updateDisplayName: { args: z.tuple([ContextRequest, RequestNickname]), result: ResultOf(PayloadProfile) },
  requestEmailChange: { args: z.tuple([ContextRequest, RequestEmail]), result: ResultVoid },
  verifyEmailChange: { args: z.tuple([ContextRequest, RequestEmailCode]), result: ResultOf(PayloadProfile) },
} as const;
const clientAuthServerConfig: IConfigZodPortContract<typeof ClientAuthServerOps> = { name: 'ClientAuthServer', ops: ClientAuthServerOps };
export const ClientAuthServerContract = zodPortContract(clientAuthServerConfig);
export type IPortClientAuthServer = PortInterface<typeof ClientAuthServerOps>;

// ── ClientLegalServer: the legal documents and the account's legal acts (FE4a) ──────────────────

/**
 * The legal REST surface: the documents (public), and the signed-in account's consent status, consents,
 * GDPR export and deletion — the token sent from inside Infrastructure, as every authenticated call since
 * FE3. Every refusal the server answered fails as `SERVER` in its own words; a server that could not be
 * asked, as `UNAVAILABLE`.
 */
const ClientLegalServerOps = {
  documents: { args: z.tuple([ContextRequest]), result: ResultOf(PayloadLegalDocuments) },
  consentStatus: { args: z.tuple([ContextRequest]), result: ResultOf(PayloadConsentStatus) },
  recordConsent: { args: z.tuple([ContextRequest, RequestRecordConsent]), result: ResultVoid },
  exportMyData: { args: z.tuple([ContextRequest]), result: ResultOf(PayloadDataExport) },
  requestAccountDeletionCode: { args: z.tuple([ContextRequest]), result: ResultVoid },
  deleteAccount: { args: z.tuple([ContextRequest, CommandDeleteAccount]), result: ResultVoid },
  /** The cookie-consent audit record (FE4b); the browser's consent id is Infrastructure's to mint. */
  recordCookieConsent: { args: z.tuple([ContextRequest, RequestCookieConsent]), result: ResultVoid },
} as const;
const clientLegalServerConfig: IConfigZodPortContract<typeof ClientLegalServerOps> = { name: 'ClientLegalServer', ops: ClientLegalServerOps };
export const ClientLegalServerContract = zodPortContract(clientLegalServerConfig);
export type IPortClientLegalServer = PortInterface<typeof ClientLegalServerOps>;

// ── ClientConsentPlatform: the consent management platform, in the browser (FE4b) ────────────────

/**
 * The CMP (Google Funding Choices, or the first-party fallback): a main-thread browser resource, handed to
 * Infrastructure by the host. `start` boots it once — StrictMode mounts twice — and from then on every
 * state it reports is DELIVERED on `ClientServerEvents` as `consentChanged` (no stream port).
 * `openPreferences` says whether the certified CMP could show its own UI; when it could not, the screen
 * opens the first-party modal. `applyManualConsent` is that modal's decision.
 */
const ClientConsentPlatformOps = {
  start: { args: z.tuple([ContextRequest]), result: ResultVoid },
  openPreferences: { args: z.tuple([ContextRequest]), result: ResultOf(DtoPreferencesOpened) },
  applyManualConsent: { args: z.tuple([ContextRequest, CommandCookiePreferences]), result: ResultVoid },
} as const;
const clientConsentPlatformConfig: IConfigZodPortContract<typeof ClientConsentPlatformOps> = { name: 'ClientConsentPlatform', ops: ClientConsentPlatformOps };
export const ClientConsentPlatformContract = zodPortContract(clientConsentPlatformConfig);
export type IPortClientConsentPlatform = PortInterface<typeof ClientConsentPlatformOps>;

// ── ClientPaymentServer: the account's credits, on the server (FE4c) ──────────────────────────────

/**
 * Stripe's checkout for a credits pack, and the account's balance — both the signed-in account's, the
 * token read inside Infrastructure (`NOT_SIGNED_IN` without one, nothing sent). The balance is read
 * once per call; how often to retry while a purchase's webhook lands is the screen's.
 */
const ClientPaymentServerOps = {
  createCheckout: { args: z.tuple([ContextRequest]), result: ResultOf(DtoCheckout) },
  balance: { args: z.tuple([ContextRequest]), result: ResultOf(DtoBalance) },
} as const;
const clientPaymentServerConfig: IConfigZodPortContract<typeof ClientPaymentServerOps> = { name: 'ClientPaymentServer', ops: ClientPaymentServerOps };
export const ClientPaymentServerContract = zodPortContract(clientPaymentServerConfig);
export type IPortClientPaymentServer = PortInterface<typeof ClientPaymentServerOps>;

// ── ClientAdsServer: the ad provider the server's A/B split chose (FE4d) ──────────────────────────

/**
 * The backend's per-player A/B decision about which ad provider this player sees — read fresh each time
 * (a cached answer would pin a returning player to a stale arm of the experiment). `config: null` when the
 * server has none.
 */
const ClientAdsServerOps = {
  adConfig: { args: z.tuple([ContextRequest, RequestAdConfig]), result: ResultOf(DtoAdConfig) },
} as const;
const clientAdsServerConfig: IConfigZodPortContract<typeof ClientAdsServerOps> = { name: 'ClientAdsServer', ops: ClientAdsServerOps };
export const ClientAdsServerContract = zodPortContract(clientAdsServerConfig);
export type IPortClientAdsServer = PortInterface<typeof ClientAdsServerOps>;

// ── ClientServerHealth: the server's /health (FE5a) ──────────────────────────────────────────────

/** One probe. Never a failure: an unreachable server is `reachable: false`, a degraded one a 503 body. */
const ClientServerHealthOps = {
  probe: { args: z.tuple([ContextRequest]), result: ResultOf(DtoHealthReport) },
} as const;
const clientServerHealthConfig: IConfigZodPortContract<typeof ClientServerHealthOps> = { name: 'ClientServerHealth', ops: ClientServerHealthOps };
export const ClientServerHealthContract = zodPortContract(clientServerHealthConfig);
export type IPortClientServerHealth = PortInterface<typeof ClientServerHealthOps>;

// ── ClientMediaServer: the table's video chat, on the server (FE5b) ──────────────────────────────

/**
 * LiveKit's signaling and admin reads — the server's side of the video chat; the SDK's room is the
 * Framework's. `token` is cached per room and player until it expires (Infrastructure's token service).
 * The connection report and the presence mark feed the usage report (§28.75).
 */
const ClientMediaServerOps = {
  token: { args: z.tuple([ContextRequest, RequestMediaToken]), result: ResultOf(DtoMediaToken) },
  reportConnection: { args: z.tuple([ContextRequest, RequestMediaConnection]), result: ResultVoid },
  markPresence: { args: z.tuple([ContextRequest, RequestMediaPresence]), result: ResultVoid },
  health: { args: z.tuple([ContextRequest, RequestMediaHealth]), result: ResultOf(DtoMediaHealth) },
  usageReport: { args: z.tuple([ContextRequest, RequestMediaUsage]), result: ResultOf(DtoMediaUsage) },
} as const;
const clientMediaServerConfig: IConfigZodPortContract<typeof ClientMediaServerOps> = { name: 'ClientMediaServer', ops: ClientMediaServerOps };
export const ClientMediaServerContract = zodPortContract(clientMediaServerConfig);
export type IPortClientMediaServer = PortInterface<typeof ClientMediaServerOps>;

// ── ClientRoundServer: the round, on the server ─────────────────────────────────────────────────

/** `startRound` at the seam: the room, and who rolls first — the host at a start, the last loser at Play Again. */
const ServerStartRound = z.object({ roomId: Identifier, rollerId: z.string() }).meta({ id: 'ServerStartRound' });
/** A claim at the seam: open or blind is the op, not a field. */
const ServerClaim = z.object({ roundId: z.string(), playerId: Identifier, score: MeyerRank }).meta({ id: 'ServerClaim' });

const ClientRoundServerOps = {
  startRound: { args: z.tuple([ContextRequest, ServerStartRound]), result: ResultOf(AnswerRound) },
  rollDice: { args: z.tuple([ContextRequest, RequestRoundMove]), result: ResultVoid },
  peekDice: { args: z.tuple([ContextRequest, RequestRoundMove]), result: ResultOf(ClientDiceRoll) },
  announceClaim: { args: z.tuple([ContextRequest, ServerClaim]), result: ResultOf(AnswerClaim) },
  announceBlind: { args: z.tuple([ContextRequest, ServerClaim]), result: ResultOf(AnswerClaim) },
  believeClaim: { args: z.tuple([ContextRequest, RequestBelieve]), result: ResultOf(AnswerRound) },
  liftCup: { args: z.tuple([ContextRequest, RequestRoundMove]), result: ResultVoid },
  /** The lobby poll's narrow read. */
  getActiveRound: { args: z.tuple([ContextRequest, RequestRoomRead]), result: ResultOf(PayloadLobbyRound.nullable()) },
  /** The resync's rich read: the pending claim's blind flag and the reveal too (2026-08-30). */
  getActiveRoundForRestore: { args: z.tuple([ContextRequest, RequestRoomRead]), result: ResultOf(PayloadActiveRound.nullable()) },
} as const;
const clientRoundServerConfig: IConfigZodPortContract<typeof ClientRoundServerOps> = { name: 'ClientRoundServer', ops: ClientRoundServerOps };
export const ClientRoundServerContract = zodPortContract(clientRoundServerConfig);
export type IPortClientRoundServer = PortInterface<typeof ClientRoundServerOps>;
export type IPayloadServerStartRound = z.infer<typeof ServerStartRound>;
export type IPayloadServerClaim = z.infer<typeof ServerClaim>;

// ── ClientRoomServer: the room, on the server ───────────────────────────────────────────────────

/** A room code, as the lookup takes it. */
const ServerRoomCode = z.object({ roomCode: z.string() }).meta({ id: 'ServerRoomCode' });
/** A seat to give up. */
const ServerLeave = z.object({ roomId: Identifier, playerId: Identifier }).meta({ id: 'ServerLeave' });

const ClientRoomServerOps = {
  /** The roster, by room id — the lobby's read on entry. */
  getRoom: { args: z.tuple([ContextRequest, RequestRoomRead]), result: ResultOf(PayloadRoomRoster) },
  /** null: no room has that code. The resync's suspended list, smart join's route and the funnel's check. */
  getRoomByCode: { args: z.tuple([ContextRequest, ServerRoomCode]), result: ResultOf(PayloadRoomCodeLookup.nullable()) },
  leaveRoom: { args: z.tuple([ContextRequest, ServerLeave]), result: ResultVoid },
  // ── taking a seat (FE2) ──
  createRoom: { args: z.tuple([ContextRequest, RequestHost]), result: ResultOf(PayloadSeatRoom) },
  joinRoom: { args: z.tuple([ContextRequest, RequestSeat]), result: ResultOf(PayloadSeatRoom) },
  lateJoinRoom: { args: z.tuple([ContextRequest, RequestLateJoin]), result: ResultOf(PayloadSeatRoom) },
  rejoinRoomByCode: { args: z.tuple([ContextRequest, RequestRejoin]), result: ResultOf(PayloadRejoin) },
  /** null: the server seats this player in no live room. */
  getActiveRoomForPlayer: { args: z.tuple([ContextRequest, RequestPlayer]), result: ResultOf(PayloadActiveRoomForPlayer.nullable()) },
} as const;
const clientRoomServerConfig: IConfigZodPortContract<typeof ClientRoomServerOps> = { name: 'ClientRoomServer', ops: ClientRoomServerOps };
export const ClientRoomServerContract = zodPortContract(clientRoomServerConfig);
export type IPortClientRoomServer = PortInterface<typeof ClientRoomServerOps>;
export type IPayloadServerRoomCode = z.infer<typeof ServerRoomCode>;

// ── ClientServerWatch: which of the server's streams reach the Application ──────────────────────

/**
 * What to watch: a room's streams (its round starts, roster, presence, countdown, views), a round's
 * (claims, rolls, lifts — the per-room topic, keyed by round as the subscriptions are), or nothing.
 * The connection's state is delivered from the first `watch` until `unwatch`.
 */
const WatchTarget = z.object({ roomId: Identifier.nullable(), roundId: z.string().nullable() }).meta({ id: 'WatchTarget' });

/**
 * IDEMPOTENT by contract: StrictMode runs an effect twice, and a target already watched must not
 * subscribe twice. `watch` sets the target and changes only the subscriptions that differ.
 *
 * The ACCOUNT watch (FE4c) is separate: a player's pushed balance (backend §28.190) is on their own
 * topic, heard whether or not they sit at a table. `watchAccount` with the same player subscribes
 * nothing again; with `null`, or `unwatchAccount`, it stops.
 */
const ClientServerWatchOps = {
  watch: { args: z.tuple([ContextRequest, WatchTarget]), result: ResultVoid },
  unwatch: { args: z.tuple([ContextRequest]), result: ResultVoid },
  watchAccount: { args: z.tuple([ContextRequest, PayloadAccountWatch]), result: ResultVoid },
  unwatchAccount: { args: z.tuple([ContextRequest]), result: ResultVoid },
} as const;
const clientServerWatchConfig: IConfigZodPortContract<typeof ClientServerWatchOps> = { name: 'ClientServerWatch', ops: ClientServerWatchOps };
export const ClientServerWatchContract = zodPortContract(clientServerWatchConfig);
export type IPortClientServerWatch = PortInterface<typeof ClientServerWatchOps>;
export type IPayloadWatchTarget = z.infer<typeof WatchTarget>;
